import React, { useRef } from "react"

import useTracking from "hooks/useTracking"

import {
  Wrapper,
  Title,
  Products,
  ProductWrapper,
  ProductMetaData,
  BuyLinkTitle,
  ProductTitle,
  Placeholder,
  ProductPrice,
  ProductDescription,
  ButtonLogoRow,
  BuyLink,
  BuyButton,
  ProductLogo,
  ImageWrapper,
  ProductImage,
  FloatingBuyLink,
  FloatingBuyButton,
  Brand,
} from "./styles"

const Shop = ({ article, element }) => {
  const { id: articleId } = article
  const { anchor, title, products } = element
  const priority = element.priority || 2
  const count = products.length

  return (
    <Wrapper id={`mbg-${anchor}`}>
      {title && <Title>{title}</Title>}
      <Products priority={priority}>
        {products.map((product, idx) => (
          <Product
            key={product.productName}
            articleId={articleId}
            product={product}
            priority={priority}
            count={count}
            idx={idx}
          />
        ))}
      </Products>
    </Wrapper>
  )
}

const Product = ({ articleId, product, priority, count, idx }) => {
  const imageSizes = {
    1: {
      1024: [367, 367],
      768: [623, 415],
      0: [480, 320],
    },
    2: {
      1024: [510, 340],
      768: [623, 415],
      0: [480, 320],
    },
    3: {
      1024: [210, 245],
      768: [623, 415],
      0: [480, 320],
    },
  }

  const { trackView, trackClick } = useTracking()
  const refWrapper = useRef(null)
  const refButton = useRef(null)
  const refButtonTitle = useRef(null)

  const trackingId = `Article Shop Unit | ${product.productName}`
  const trackingProps = {
    article_id: articleId,
    category: "Content Engagement",
    label: `${articleId}|${product.trackProduct}`,
    product_id: product.productId,
    product: product.trackProduct,
  }

  trackView(refWrapper, trackingId, trackingProps)
  trackClick(refButton, trackingId, trackingProps)
  trackClick(refButtonTitle, trackingId, trackingProps)

  const rel = product.url?.match(/shop\.mindbodygreen/) ? "sponsored" : "nofollow"

  return (
    <ProductWrapper ref={refWrapper} priority={priority} count={count} idx={idx}>
      <ProductMetaData reverse={idx % 2 === 1} priority={priority}>
        {product.brand && <Brand priority={priority}>{product.brand}</Brand>}
        {priority === 2 ? (
          <BuyLinkTitle
            ref={refButtonTitle}
            href={product.url}
            target="_blank"
            rel={rel}
            {...(product.nofollowUrl && { rel: "noreferrer nofollow" })}
            priority={priority}
          >
            <ProductTitle
              priority={priority}
              dangerouslySetInnerHTML={{ __html: product.productName }}
            />
          </BuyLinkTitle>
        ) : (
          <ProductTitle
            priority={priority}
            dangerouslySetInnerHTML={{ __html: product.productName }}
          />
        )}
        <Placeholder priority={priority}></Placeholder>
        <ProductPrice priority={priority}>
          {product.price
            ? product.price.toString().includes("$")
              ? product.price
              : `$ ${product.price}`
            : ""}
        </ProductPrice>
        <div>
          {(priority === 1 || priority === 3) && (
            <ProductDescription
              dangerouslySetInnerHTML={{
                __html: product.description,
              }}
            />
          )}
          <ButtonLogoRow priority={priority}>
            <BuyLink
              href={product.url}
              target="_blank"
              rel={rel}
              {...(product.nofollowUrl && { rel: "noreferrer nofollow" })}
              priority={priority}
            >
              <BuyButton ref={refButton}>{product.buttonText || "BUY NOW"}</BuyButton>
            </BuyLink>
            {product.logo && (
              <ProductLogo
                fetchpriority="low"
                imageData={product.logo}
                lazy
                sizes={[
                  {
                    width: 113,
                    height: 99999,
                    transforms: "c_scale,w_113,q_auto,f_auto,fl_lossy",
                  },
                ]}
                alt={product.logo.altText}
              />
            )}
          </ButtonLogoRow>
        </div>
      </ProductMetaData>
      <ImageWrapper priority={priority} reverse={idx % 2 === 1}>
        {product.image && (
          <ProductImage
            fetchpriority="low"
            priority={priority}
            imageData={product.image}
            lazy
            sizes={[
              {
                query: 1024,
                width: imageSizes[priority][1024][0],
                height: imageSizes[priority][1024][1],
                transforms: `c_fill,g_auto:thirds_0,w_${imageSizes[priority][1024][0]},h_${imageSizes[priority][1024][1]},q_auto,f_auto,fl_lossy`,
              },
              {
                query: 768,
                width: imageSizes[priority][768][0],
                height: imageSizes[priority][768][1],
                transforms: `c_fill,g_auto:thirds_0,w_${imageSizes[priority][768][0]},h_${imageSizes[priority][768][1]},q_auto,f_auto,fl_lossy`,
              },
              {
                width: imageSizes[priority][0][0],
                height: imageSizes[priority][0][1],
                transforms: `c_fill,g_auto:thirds_0,w_${imageSizes[priority][0][0]},h_${imageSizes[priority][0][1]},q_auto,f_auto,fl_lossy`,
              },
            ]}
            alt={product.image.altText}
            contain
          />
        )}
        {priority === 2 && (
          <FloatingBuyLink
            href={product.url}
            target="_blank"
            rel={rel}
            {...(product.nofollowUrl && { rel: "noreferrer nofollow" })}
            priority={priority}
          >
            <FloatingBuyButton ref={refButton}>{product.buttonText || "BUY NOW"}</FloatingBuyButton>
          </FloatingBuyLink>
        )}
      </ImageWrapper>
    </ProductWrapper>
  )
}

export default Shop
